import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./styles/ScenarioCard.module.css";
import blankImage from "../../../images/blankimage.png";
import { calculateTimeSince } from "../../../utils/utils";
import axios from "axios";
import AuthContext from "../../../context/AuthContext";
import ShareModal from "./ShareModal";
import TransferModal from "./TransferModal";
import ScenarioCardOptions from "./ScenarioCardOptions";
import ScenarioCardInfo from "./ScenarioCardInfo";
import ScenarioCardTags from "./ScenarioCardTags";

const ScenarioCard = ({
    scenario,
    isAuthor,
    onDeleteScenario,
    onCopyScenario,
    searchQuery,
}) => {
    const navigate = useNavigate();
    const scenarioId = scenario._id;
    const { userId } = useContext(AuthContext);

    
    const [likesCount, setLikesCount] = useState(scenario.likedBy?.length ?? 0);
    const [hasBeenLiked, setHasBeenLiked] = useState(false);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [copySuccess, setCopySuccess] = useState("");
    const [openedDropdown, setOpenedDropdown] = useState(null);
    const [isHovered, setHovered] = useState(false);
    const [showTagsView, setShowTagsView] = useState(false);

    
    useEffect(() => {
        setLikesCount(scenario.likedBy?.length ?? 0);
        if (userId) {
            setHasBeenLiked(scenario.likedBy?.includes(userId) ?? false);
        } else {
            setHasBeenLiked(false);
        }
    }, [scenario.likedBy, userId]);

    useEffect(() => {
        setShowTagsView(false);
        setIsImageLoaded(false);
    }, [scenario._id]);


    
    const handleClickLikeScenario = async () => {
        if (!scenarioId || !userId || !scenario.isPublished) return;

        const previousLikedState = hasBeenLiked;
        const previousLikesCount = likesCount;

        
        setHasBeenLiked(!previousLikedState);
        setLikesCount(previousLikedState ? previousLikesCount - 1 : previousLikesCount + 1);

        try {
            await axios.patch(
                `${process.env.REACT_APP_API_URL_LOCAL}/api/scenarios/likeScenario/${scenarioId}`
            );
        } catch (error) {
            console.error("Error liking scenario: ", error);
            setHasBeenLiked(previousLikedState);
            setLikesCount(previousLikesCount);
        }
    };

    const highlightText = (text, query) => {
        if (!text) return "";
        if (!query) return text;

        try {
            const parts = text.split(new RegExp(`(${query.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')})`, "gi"));
            return parts.map((part, index) =>
                part && query && part.toLowerCase() === query.toLowerCase() ? (
                    <span key={index} className={styles.highlight}>
                        {part}
                    </span>
                ) : (
                    part
                )
            );
        } catch (e) {
            console.error("Error creating regex for highlighting:", e);
            return text;
        }
    };


    const handleTagClick = (tag) => {
        navigate(`/browse/${encodeURIComponent(tag)}`);
    };


    const renderImage = () => {
        const imageUrl = scenario.image || blankImage;
        const imageStyle = {
            cursor: scenario.isPublished ? "pointer" : "default"
        };

        const handleImageLoad = () => {
            
            setIsImageLoaded(true);
        };

        const handleImageError = (e) => {
            
            setIsImageLoaded(true);
            if (e.target.src !== blankImage) {
            e.target.src = blankImage;
            }
        };

        return (
            <img
                src={imageUrl}
                className={`${styles.image} ${isImageLoaded ? styles.imageLoaded : ''}`}
                onLoad={handleImageLoad}
                onError={handleImageError}
                alt={scenario.title || "Scenario image"}
                style={imageStyle}
                onClick={() => scenario.isPublished && navigate(`/view/${scenario._id}`)}
            />
        );
    };

    const shareUrl = `${window.location.origin}/view/${scenario._id}`;

    return (
        <>
            {/* article tag for semantic card structure */}
            <article className={styles.card} aria-labelledby={`scenario-title-${scenario._id}`}>
                {isAuthor && (
                    <div className={styles.optionsButtonContainer}>
                        <button
                            className={styles.optionsButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpenedDropdown(
                                    openedDropdown === scenario._id ? null : scenario._id
                                );
                            }}
                            aria-haspopup="true"
                            aria-expanded={openedDropdown === scenario._id}
                            aria-label="Scenario Options"
                        >
                            ⋮
                        </button>
                        {openedDropdown === scenario._id && (
                            <ScenarioCardOptions
                                scenario={scenario}
                                onClose={() => setOpenedDropdown(null)}
                                onDeleteScenario={onDeleteScenario}
                                onCopyScenario={onCopyScenario}
                                navigate={navigate}
                                
                                
                            />
                        )}
                    </div>
                )}

                {/* Main content area */}
                <div className={`${styles.contentWrapper} ${!scenario.isPublished ? styles.unpublished : ""}`}>
                     {/* Image Section */}
                     <div className={styles.imageContainer}>
                        {!scenario.isPublished ? (
                            <div className={styles.tooltipContainer}>
                                {renderImage()}
                                <span className={styles.exclamationIcon} aria-hidden="true">!</span>
                                <div className={styles.tooltipText} role="tooltip">
                                    This Scenario is not published
                                </div>
                            </div>
                        ) : (
                            renderImage()
                        )}
                    </div>

                    {/* Dynamic Bottom Section: Info or Tags */}
                    <div className={styles.bottomSection}>
                        {showTagsView ? (
                            <ScenarioCardTags
                                tags={scenario.tags || []}
                                searchQuery={searchQuery}
                                highlightText={highlightText}
                                handleTagClick={handleTagClick}
                                onClose={() => setShowTagsView(false)}
                            />
                        ) : (
                            <ScenarioCardInfo
                                scenario={scenario}
                                userId={userId}
                                likesCount={likesCount}
                                hasBeenLiked={hasBeenLiked}
                                isHovered={isHovered}
                                searchQuery={searchQuery}
                                highlightText={highlightText}
                                handleClickLikeScenario={handleClickLikeScenario}
                                setShowShareModal={setShowShareModal}
                                onShowTags={() => setShowTagsView(true)}
                                setHovered={setHovered}
                            />
                        )}
                    </div>
                </div>
            </article>

            {/* --- Modals --- */}
            {showShareModal && (
                <ShareModal
                    handleCloseShare={() => setShowShareModal(false)}
                    onCopy={() => {
                        setCopySuccess("Copied!");
                        setTimeout(() => setCopySuccess(""), 2000);
                    }}
                    copySuccess={copySuccess}
                    shareUrl={shareUrl}
                />
            )}
            {showTransferModal && (
                <TransferModal
                    handleCloseTransfer={() => setShowTransferModal(false)}
                    scenario={scenario}
                />
            )}
        </>
    );
};

export default ScenarioCard;